import { notificationService } from './notificationService'

/**
 * Service to integrate notifications with various app events
 */
class NotificationIntegration {
  
  /**
   * Trigger notification when user registers for an event
   */
  static onEventRegistration(eventTitle: string, eventDate: string, eventId?: string) {
    notificationService.addNotification({
      type: 'success',
      title: 'Registration Confirmed',
      message: `Your registration for "${eventTitle}" has been confirmed for ${new Date(eventDate).toLocaleDateString()}.`,
      priority: 'medium',
      category: 'registration',
      actionUrl: eventId ? `/events/${eventId}` : '/events',
      actionText: 'View Event'
    })
  }

  /**
   * Trigger notification when payment is successful
   */
  static onPaymentSuccess(amount: number, description: string) {
    notificationService.addNotification({
      type: 'success',
      title: 'Payment Successful',
      message: `Your payment of R${amount.toFixed(2)} for ${description} has been processed successfully.`,
      priority: 'medium',
      category: 'payments',
      metadata: { amount }
    })
  }

  /**
   * Trigger notification when payment fails
   */
  static onPaymentFailure(amount: number, description: string) {
    notificationService.addNotification({
      type: 'error',
      title: 'Payment Failed',
      message: `Your payment of R${amount.toFixed(2)} for ${description} could not be processed. Please try again.`,
      priority: 'urgent',
      category: 'payments',
      actionUrl: '/dashboard',
      actionText: 'Retry Payment',
      metadata: { amount }
    })
  }

  /**
   * Trigger notification when user becomes a coordinator
   */
  static onCoordinatorUpgrade(credits: number) {
    notificationService.addNotification({
      type: 'success',
      title: 'Coordinator Upgrade Complete',
      message: `Congratulations! You are now an event coordinator with ${credits} event creation credit${credits !== 1 ? 's' : ''}.`,
      priority: 'high',
      category: 'coordinator',
      actionUrl: '/coordinator/events',
      actionText: 'Create Event'
    })
  }

  /**
   * Trigger notification when coordinator creates an event
   */
  static onEventCreated(eventTitle: string, eventId: string) {
    notificationService.addNotification({
      type: 'success',
      title: 'Event Created Successfully',
      message: `Your event "${eventTitle}" has been created and is now live for registrations.`,
      priority: 'medium',
      category: 'events',
      actionUrl: `/events/${eventId}`,
      actionText: 'View Event'
    })
  }

  /**
   * Trigger notification when event registration deadline is approaching
   */
  static onEventDeadlineApproaching(eventTitle: string, daysLeft: number, eventId: string) {
    notificationService.addNotification({
      type: 'warning',
      title: 'Event Deadline Approaching',
      message: `Registration for "${eventTitle}" closes in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}. Don't miss out!`,
      priority: 'high',
      category: 'events',
      actionUrl: `/events/${eventId}`,
      actionText: 'Register Now',
      expiresAt: new Date(Date.now() + daysLeft * 24 * 60 * 60 * 1000)
    })
  }

  /**
   * Trigger notification when new event is available
   */
  static onNewEventAvailable(eventTitle: string, eventDate: string, eventId: string) {
    notificationService.addNotification({
      type: 'event',
      title: 'New Event Available',
      message: `"${eventTitle}" registration is now open for ${new Date(eventDate).toLocaleDateString()}!`,
      priority: 'high',
      category: 'events',
      actionUrl: `/events/${eventId}`,
      actionText: 'View Event'
    })
  }

  /**
   * Trigger notification for system maintenance
   */
  static onSystemMaintenance(startTime: string, endTime: string) {
    notificationService.addNotification({
      type: 'info',
      title: 'Scheduled Maintenance',
      message: `System maintenance is scheduled from ${startTime} to ${endTime}. Some features may be temporarily unavailable.`,
      priority: 'low',
      category: 'system'
    })
  }

  /**
   * Trigger notification when coordinator runs out of credits
   */
  static onCreditsExhausted() {
    notificationService.addNotification({
      type: 'warning',
      title: 'Event Credits Exhausted',
      message: 'You have used all your event creation credits. Purchase more credits to continue creating events.',
      priority: 'high',
      category: 'coordinator',
      actionUrl: '/dashboard',
      actionText: 'Buy Credits'
    })
  }

  /**
   * Trigger notification when event reaches capacity
   */
  static onEventCapacityReached(eventTitle: string, eventId: string) {
    notificationService.addNotification({
      type: 'info',
      title: 'Event Full',
      message: `"${eventTitle}" has reached maximum capacity. Registration is now closed.`,
      priority: 'medium',
      category: 'events',
      actionUrl: `/events/${eventId}`,
      actionText: 'View Event'
    })
  }

  /**
   * Trigger notification when user profile needs updating
   */
  static onProfileUpdateRequired(reason: string) {
    notificationService.addNotification({
      type: 'warning',
      title: 'Profile Update Required',
      message: `Please update your profile: ${reason}`,
      priority: 'medium',
      category: 'general',
      actionUrl: '/profile',
      actionText: 'Update Profile'
    })
  }

  /**
   * Trigger welcome notification for new users
   */
  static onUserWelcome(userName: string) {
    notificationService.addNotification({
      type: 'info',
      title: `Welcome to GOMTB, ${userName}!`,
      message: 'Explore upcoming mountain biking events and join our community of riders.',
      priority: 'low',
      category: 'general',
      actionUrl: '/events',
      actionText: 'Browse Events'
    })
  }

      /**
     * Clear all notifications (for user request)
   */
  static clearAllNotifications() {
    notificationService.clearAllNotifications()
  }


}

export { NotificationIntegration } 